import { useFeatureFlags } from '@brightdrop/feature-flags-client';
import { useFormat } from '@brightdrop/localization-core';
import { LinearProgress, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import FullscreenExpandIcon from '~/assets/icons/new/video-controls/fullscreen-sm.svg?react';
import FullscreenCollapseIcon from '~/assets/icons/new/video-controls/fullscreenExit-sm.svg?react';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';
import { LOCALIZATION_FEATURE_FLAG } from '~/common/models/featureFlags.model';

import useStyles from './ActivationProgressSnackbar.styles';
import { getBatchSelectionTotal } from './batch.utils';
import type { ActivationBatch } from './state/activationProgressSlice';

const TRANSLATION_KEYS = {
  title: 'catalog:order.activationProgress.title',
  titleFallback: 'catalog:order.activationProgress.titleFallback',
  started: 'catalog:order.activationProgress.started',
  countProgress: 'catalog:order.activationProgress.countProgress',
  minimize: 'catalog:order.activationProgress.minimize',
  expand: 'catalog:order.activationProgress.expand',
} as const;

const computePercent = (total: number, done: number): number => {
  if (total <= 0) return 0;
  return Math.min(100, Math.round((done / total) * 100));
};

/**
 * Items already resolved for the batch. Re-enrollments settle with the billing
 * order, before any polling starts, so they count as done from the outset — a
 * mixed activation of one new device plus two re-enrollments opens at 2/3.
 */
const getDoneCount = (batch: ActivationBatch): number =>
  batch.success + batch.failure + (batch.reenrollmentCount ?? 0);

export type ActivationProgressCardProps = {
  /** In-progress bulk batches, one row each. */
  batches: ActivationBatch[];
};

/**
 * In-progress state of the activation widget: a collapsible card that stacks
 * one "started-at + progress bar + done/total" row per running bulk batch.
 */
export const ActivationProgressCard = ({
  batches,
}: ActivationProgressCardProps): JSX.Element => {
  const { classes } = useStyles();
  const { getFlag } = useFeatureFlags();
  const localizationEnabled = Boolean(getFlag(LOCALIZATION_FEATURE_FLAG));
  const format = useFormat();
  // Subscribe to i18n so imperative `translateText(...)` re-renders on locale
  // change.
  useTranslation(['catalog']);
  const [isMinimized, setIsMinimized] = useState(false);

  const productName = batches.find((batch) => batch.productName)?.productName;
  const progressTitle = batches.find(
    (batch) => batch.progressTitle
  )?.progressTitle;
  const title =
    progressTitle ??
    (productName
      ? translateText(TRANSLATION_KEYS.title, { productName })
      : translateText(TRANSLATION_KEYS.titleFallback));

  return (
    <section
      className={classes.root}
      aria-live="polite"
      aria-label={title}
      data-testid="activation-progress-snackbar"
    >
      <div className={classes.header}>
        <Typography
          variant="header2_medium"
          className={classes.title}
          data-testid="activation-progress-title"
        >
          {title}
        </Typography>
        <button
          type="button"
          className={classes.iconButton}
          onClick={() => setIsMinimized((prev) => !prev)}
          aria-expanded={!isMinimized}
          aria-label={translateText(
            isMinimized ? TRANSLATION_KEYS.expand : TRANSLATION_KEYS.minimize
          )}
          data-testid="activation-progress-toggle"
        >
          {isMinimized ? (
            <FullscreenExpandIcon aria-hidden="true" focusable={false} />
          ) : (
            <FullscreenCollapseIcon aria-hidden="true" focusable={false} />
          )}
        </button>
      </div>

      {!isMinimized && (
        <div className={classes.body}>
          {batches.map((batch) => (
            <div
              key={batch.batchId}
              className={classes.batchRow}
              data-testid="activation-progress-row"
            >
              <Typography
                variant="caption_regular"
                className={classes.startedAt}
                data-testid="activation-progress-started"
              >
                {translateText(TRANSLATION_KEYS.started, {
                  time: localizationEnabled
                    ? format.time(new Date(batch.startedAt))
                    : new Date(batch.startedAt).toLocaleTimeString(undefined, {
                        hour: 'numeric',
                        minute: '2-digit',
                      }),
                })}
              </Typography>
              <div className={classes.progressRow}>
                <LinearProgress
                  variant="determinate"
                  value={computePercent(
                    getBatchSelectionTotal(batch),
                    getDoneCount(batch)
                  )}
                  className={classes.progressBar}
                  data-testid="activation-progress-bar"
                />
                <Typography
                  variant="caption_regular"
                  className={classes.countLabel}
                  data-testid="activation-progress-count"
                >
                  {translateText(TRANSLATION_KEYS.countProgress, {
                    done: getDoneCount(batch),
                    // Counts everything the user submitted, so the denominator
                    // matches the selection rather than only the polled devices.
                    total: getBatchSelectionTotal(batch),
                  })}
                </Typography>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default ActivationProgressCard;
